import {
  Button,
  Card,
  Col,
  DatePicker,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Typography as Text,
} from 'antd'
import enUS from 'antd/lib/date-picker/locale/en_US'
import moment from 'moment'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { currencyWithCommas } from '@/libs/numberWithCommas'
import { isRegion, REGIONS } from '@/libs/region'
import thTH from '@/locale/th_TH'
import zhTW from '@/locale/zh_TW'
import { useSelector } from '@/redux'

const { RangePicker } = DatePicker
const { Option } = Select

const antdLocaleMap = {
  'en-US': enUS,
  'zh-HK': zhTW,
  'th-TH': thTH,
}

/**
 * 發票查詢共用搜尋區塊（由 EinvoiceInvoices 抽出，供 EinvoiceInvoices 與 TaxInvoiceReports 共用）。
 * @param {Object} props
 * @param {{ status?, orderSerial?, invoiceNo?, deviceId?, startedAt?, endedAt?, limit? }} props.filters
 * @param {(patch: Object) => void} props.onChange
 * @param {() => void} props.onSearch
 * @param {() => void} props.onReset
 * @param {boolean} [props.showStatus=true]
 * @param {boolean} [props.showOrderSerial=true]
 * @param {boolean} [props.showInvoiceNo=true]
 * @param {boolean} [props.showTerminalId=true]
 * @param {boolean} [props.showDateRange=true]
 * @param {boolean} [props.showPageLimit=true]
 * @param {boolean} [props.searchDisabled=false]
 * @param {number} [props.totalCount]
 * @param {number} [props.totalAmount]
 */
export default function InvoiceSearchPanel(props) {
  const {
    filters,
    onChange,
    onSearch,
    onReset,
    showStatus = true,
    showOrderSerial = true,
    showInvoiceNo = true,
    showTerminalId = true,
    showDateRange = true,
    showPageLimit = true,
    searchDisabled = false,
    totalCount,
    totalAmount,
  } = props
  const { t } = useTranslation()
  const currentLang = useSelector((state) => state.app.lang)
  const antdLocale = antdLocaleMap[currentLang] || enUS
  const activeMerchant = useSelector((state) => state.merchant.activeMerchant)
  const isTH = isRegion(activeMerchant?.region, REGIONS.TH)
  const currency = activeMerchant?.setting?.currency
  const minFractionDigits = activeMerchant?.rounding?.minFractionDigits

  const quickRanges = React.useMemo(
    () => ({
      [t('page.einvoiceInvoices.quickRanges.today')]: [
        moment().startOf('day'),
        moment().endOf('day').seconds(0).milliseconds(0),
      ],
      [t('page.einvoiceInvoices.quickRanges.yesterday')]: [
        moment().subtract(1, 'day').startOf('day'),
        moment().subtract(1, 'day').endOf('day').seconds(0).milliseconds(0),
      ],
      [t('page.einvoiceInvoices.quickRanges.thisMonth')]: [
        moment().startOf('month').startOf('day'),
        moment().endOf('day').seconds(0).milliseconds(0),
      ],
      [t('page.einvoiceInvoices.quickRanges.lastMonth')]: [
        moment().subtract(1, 'month').startOf('month').startOf('day'),
        moment().subtract(1, 'month').endOf('month').seconds(0).milliseconds(0),
      ],
      [t('page.einvoiceInvoices.quickRanges.thisYear')]: [
        moment().startOf('year').startOf('day'),
        moment().endOf('day').seconds(0).milliseconds(0),
      ],
      [t('page.einvoiceInvoices.quickRanges.last7Days')]: [
        moment().subtract(6, 'days').startOf('day'),
        moment().endOf('day').seconds(0).milliseconds(0),
      ],
      [t('page.einvoiceInvoices.quickRanges.last30Days')]: [
        moment().subtract(29, 'days').startOf('day'),
        moment().endOf('day').seconds(0).milliseconds(0),
      ],
    }),
    [t],
  )

  const onDateRangeChange = (values) => {
    if (!values || values.length !== 2) {
      onChange({ startedAt: undefined, endedAt: undefined })
      return
    }
    const [start, end] = values
    onChange({
      startedAt: start ? moment(start).seconds(0).milliseconds(0).toISOString() : undefined,
      endedAt: end ? moment(end).seconds(0).milliseconds(0).toISOString() : undefined,
    })
  }

  return (
    <>
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={12}>
          {!isTH && showStatus && (
            <Col span={6}>
              <Text>{t('page.einvoiceInvoices.status')}</Text>
              <Select
                allowClear
                style={{ width: '100%' }}
                value={filters.status}
                onChange={(v) => onChange({ status: v })}
              >
                <Option value='pending'>{t('page.einvoiceInvoices.statusPending')}</Option>
                <Option value='issued'>{t('page.einvoiceInvoices.statusIssued')}</Option>
                <Option value='voided'>{t('page.einvoiceInvoices.statusVoided')}</Option>
                <Option value='refunded'>{t('page.einvoiceInvoices.statusRefunded')}</Option>
              </Select>
            </Col>
          )}
          {showOrderSerial && (
            <Col span={6}>
              <Text>{t('page.einvoiceInvoices.orderSerial')}</Text>
              <Input
                placeholder={t('page.einvoiceInvoices.orderSerial')}
                value={filters.orderSerial}
                onChange={(e) => onChange({ orderSerial: e.target.value })}
              />
            </Col>
          )}
          {showInvoiceNo && (
            <Col span={6}>
              <Text>{t('page.einvoiceInvoices.invoiceNo')}</Text>
              <Input
                placeholder={t('page.einvoiceInvoices.invoiceNo')}
                value={filters.invoiceNo}
                onChange={(e) => onChange({ invoiceNo: e.target.value })}
              />
            </Col>
          )}
          {!isTH && showTerminalId && (
            <Col span={6}>
              <Text>{t('page.einvoiceInvoices.terminalId')}</Text>
              <Input
                placeholder={t('page.einvoiceInvoices.terminalId')}
                value={filters.deviceId}
                onChange={(e) => onChange({ deviceId: e.target.value })}
              />
            </Col>
          )}
        </Row>
        <Row gutter={12} style={{ marginTop: 12 }}>
          {showDateRange && (
            <Col span={12}>
              <Text>{t('page.einvoiceInvoices.createdAtRange')}</Text>
              <RangePicker
                showTime={{
                  format: 'HH:mm',
                  defaultValue: [moment('00:00', 'HH:mm'), moment('23:59', 'HH:mm')],
                }}
                format='YYYY/MM/DD HH:mm'
                locale={antdLocale}
                ranges={quickRanges}
                style={{ width: '100%' }}
                value={
                  filters.startedAt && filters.endedAt
                    ? [moment(filters.startedAt), moment(filters.endedAt)]
                    : undefined
                }
                onChange={onDateRangeChange}
              />
            </Col>
          )}
          {showPageLimit && (
            <Col span={4}>
              <Text>{t('page.einvoiceInvoices.pageLimit')}</Text>
              <InputNumber
                min={1}
                max={50}
                style={{ width: '100%' }}
                value={filters.limit}
                onChange={(v) => onChange({ limit: Math.min(v || 1, 50) })}
              />
            </Col>
          )}
          <Col span={8} style={{ display: 'flex', alignItems: 'flex-end' }}>
            <Space>
              <Button type='primary' disabled={searchDisabled} onClick={onSearch}>
                {t('app.common.search')}
              </Button>
              <Button onClick={onReset}>{t('app.common.reset')}</Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {totalCount != null && (
        <Card style={{ marginBottom: 16 }}>
          <Space size='large'>
            <Text>
              {t('page.einvoiceInvoices.totalCount')}
              {`: ${totalCount.toLocaleString()}`}
            </Text>
            <Text>
              {t('page.einvoiceInvoices.totalAmount')}
              {`: ${totalAmount != null
                  ? currencyWithCommas(totalAmount, currency, minFractionDigits)
                  : '-'
                }`}
            </Text>
          </Space>
        </Card>
      )}
    </>
  )
}
